import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Package, Plus, ArrowRight, CheckCircle, Clock, Airplane, MapPin, WarningCircle, CalendarBlank, X, ArrowLeft, DeviceMobile, ShareNetwork, PlusCircle, Export, BellRinging } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { usePortalLang } from '../hooks/usePortalLang';
import { PortalLanguageToggle } from '../components/PortalLanguageToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TOUR_STEPS = [
  { target: 'tracking-input', title: 'Enter Tracking Number', desc: 'Paste or type your package tracking number here. That\'s all you need — just the tracking number!', position: 'bottom' },
  { target: 'submit-btn', title: 'Submit', desc: 'Tap here to add the package. Your courier will handle the rest!', position: 'top' },
  { target: 'packages-list', title: 'Track Your Packages', desc: 'All your submitted packages appear here with live status updates. Check back anytime!', position: 'top' },
];

function TourOverlay({ step, totalSteps, currentStep, onNext, onSkip, primaryColor }) {
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect(rect);
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [step.target]);

  if (!targetRect) return null;

  const tooltipStyle = {
    position: 'fixed',
    left: Math.min(Math.max(targetRect.left, 16), window.innerWidth - 320),
    top: step.position === 'bottom' ? targetRect.bottom + 12 : targetRect.top - 180,
    zIndex: 10001,
  };

  return (
    <div className="fixed inset-0 z-[10000]" data-testid="tour-overlay">
      <div className="absolute inset-0 bg-black/60" onClick={onSkip} />
      <div
        className="absolute border-2 rounded-xl pointer-events-none"
        style={{
          left: targetRect.left - 6, top: targetRect.top - 6,
          width: targetRect.width + 12, height: targetRect.height + 12,
          borderColor: primaryColor,
          boxShadow: `0 0 0 4000px rgba(0,0,0,0.6), 0 0 20px ${primaryColor}50`,
          zIndex: 10001,
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: step.position === 'bottom' ? -10 : 10 }}
        animate={{ opacity: 1, y: 0 }}
        style={tooltipStyle}
        className="bg-[#111827] border border-white/10 rounded-2xl p-5 w-[300px] shadow-2xl"
      >
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-bold text-white text-sm">{step.title}</h3>
          <button onClick={onSkip} className="text-gray-500 hover:text-white p-0.5"><X size={14} /></button>
        </div>
        <p className="text-gray-400 text-xs mb-4 leading-relaxed">{step.desc}</p>
        <div className="flex items-center justify-between">
          <span className="text-gray-600 text-xs">{currentStep + 1} / {totalSteps}</span>
          <Button onClick={onNext} className="h-8 px-4 rounded-lg text-xs text-white" style={{ background: primaryColor }} data-testid="tour-next-btn">
            {currentStep === totalSteps - 1 ? 'Got it!' : 'Next'}
            <ArrowRight size={12} className="ml-1" />
          </Button>
        </div>
      </motion.div>
    </div>
  );
}

function AddToHomeScreenBanner({ primaryColor, onDismiss }) {
  const [isIOS] = useState(/iphone|ipad|ipod/i.test(navigator.userAgent));
  const [isAndroid] = useState(/android/i.test(navigator.userAgent));

  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed bottom-0 left-0 right-0 z-50 p-4"
      data-testid="add-to-home-banner"
    >
      <div className="max-w-lg mx-auto bg-[#111827] border border-white/10 rounded-2xl p-5 shadow-2xl shadow-black/50">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: primaryColor + '20' }}>
            <DeviceMobile size={24} style={{ color: primaryColor }} weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-white font-bold text-sm mb-1">Save to Home Screen</h3>
            <p className="text-gray-400 text-xs leading-relaxed mb-3">
              Add this app to your home screen for quick access to submit tracking numbers and check package status.
            </p>
            {isIOS ? (
              <div className="flex items-center gap-2 text-xs text-gray-300 bg-white/5 rounded-lg px-3 py-2">
                <Export size={16} style={{ color: primaryColor }} />
                <span>Tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong></span>
              </div>
            ) : isAndroid ? (
              <div className="flex items-center gap-2 text-xs text-gray-300 bg-white/5 rounded-lg px-3 py-2">
                <PlusCircle size={16} style={{ color: primaryColor }} />
                <span>Tap <strong>Menu (3 dots)</strong> then <strong>"Add to Home Screen"</strong></span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-gray-300 bg-white/5 rounded-lg px-3 py-2">
                <PlusCircle size={16} style={{ color: primaryColor }} />
                <span>Bookmark this page for easy access</span>
              </div>
            )}
          </div>
          <button onClick={onDismiss} className="text-gray-500 hover:text-white p-1 flex-shrink-0">
            <X size={18} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

const STATUS_FLOW = [
  { key: 'submitted', icon: Plus, color: 'bg-blue-500', label: 'Submitted' },
  { key: 'intake_miami', icon: CheckCircle, color: 'bg-emerald-500', label: 'Received at Warehouse' },
  { key: 'in_transit', icon: Airplane, color: 'bg-amber-500', label: 'In Transit' },
  { key: 'arrived_san_jose', icon: MapPin, color: 'bg-emerald-500', label: 'Arrived at Destination' },
  { key: 'delivered', icon: CheckCircle, color: 'bg-emerald-600', label: 'Delivered' },
];

function getStatusIndex(status) {
  const idx = STATUS_FLOW.findIndex(s => s.key === status);
  return idx >= 0 ? idx : 0;
}

function StatusBar({ status, warehouseName, destinationName }) {
  const currentIdx = getStatusIndex(status);
  const steps = STATUS_FLOW.map((s, i) => {
    let label = s.label;
    if (s.key === 'intake_miami' && warehouseName) label = `Received: ${warehouseName}`;
    if (s.key === 'arrived_san_jose' && destinationName) label = `Arrived: ${destinationName}`;
    return { ...s, label, active: i <= currentIdx };
  });

  return (
    <div className="flex items-center gap-0 w-full" data-testid="status-bar">
      {steps.map((step, i) => (
        <div key={step.key} className="flex items-center flex-1">
          <div className="flex flex-col items-center relative">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs transition-all ${step.active ? step.color : 'bg-gray-700'}`}>
              <step.icon size={14} weight="bold" />
            </div>
            <span className={`text-[9px] mt-1 text-center leading-tight max-w-[70px] ${step.active ? 'text-white font-medium' : 'text-gray-600'}`}>{step.label}</span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 rounded transition-all ${i < currentIdx ? steps[i + 1].color : 'bg-gray-700'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

export default function ClientSubmitPage() {
  const { linkId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = usePortalLang();
  const [info, setInfo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [error, setError] = useState('');
  const [tourStep, setTourStep] = useState(-1);
  const [showHomeBanner, setShowHomeBanner] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushDismissed, setPushDismissed] = useState(false);

  useEffect(() => { loadData(); }, [linkId]);

  useEffect(() => {
    if (!loading && info) {
      if (searchParams.get('tour') === '1') {
        setTimeout(() => setTourStep(0), 800);
        // Show home screen banner after tour
        setTimeout(() => {
          const dismissed = localStorage.getItem(`home_banner_${linkId}`);
          if (!dismissed) setShowHomeBanner(true);
        }, 8000);
      } else {
        const dismissed = localStorage.getItem(`home_banner_${linkId}`);
        if (!dismissed) setTimeout(() => setShowHomeBanner(true), 2000);
      }
    }
  }, [loading, info, searchParams, linkId]);

  const loadData = async () => {
    try {
      const [infoRes, packagesRes] = await Promise.all([
        axios.get(`${API_URL}/api/portal/submit/${linkId}`),
        axios.get(`${API_URL}/api/portal/track/${linkId}`),
      ]);
      setInfo(infoRes.data);
      setPackages(packagesRes.data.packages || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired tracking link');
    } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) { toast.error(t('Please enter a tracking number', 'Por favor ingresa un número de tracking')); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/portal/submit/${linkId}`, {
        tracking_number: trackingNumber.trim(),
        description: '',
        weight_lbs: 0,
      });
      toast.success(t('Package submitted!', '¡Paquete enviado!'));
      setTrackingNumber('');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Submission failed', 'Error al enviar'));
    } finally { setSubmitting(false); }
  };

  const dismissBanner = () => {
    setShowHomeBanner(false);
    localStorage.setItem(`home_banner_${linkId}`, '1');
  };

  const requestPushNotifications = async () => {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        toast.error('Push notifications are not supported on this device');
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Permission denied. Enable notifications in your browser settings.');
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      if (!vapidKey) { toast.error('Push notifications not configured'); return; }
      const urlBase64 = vapidKey.replace(/-/g, '+').replace(/_/g, '/');
      const padding = '='.repeat((4 - urlBase64.length % 4) % 4);
      const raw = atob(urlBase64 + padding);
      const uint8 = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; ++i) uint8[i] = raw.charCodeAt(i);

      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: uint8 });
      const subJson = sub.toJSON();
      await axios.post(`${API_URL}/api/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
        tracking_link_id: linkId,
        customer_id: info?.customer_id || '',
      });
      setPushEnabled(true);
      localStorage.setItem(`push_enabled_${linkId}`, '1');
      toast.success('Push notifications enabled! You\'ll be notified when packages update.');
    } catch (err) {
      toast.error('Failed to enable push notifications');
    }
  };

  useEffect(() => {
    const enabled = localStorage.getItem(`push_enabled_${linkId}`);
    if (enabled) setPushEnabled(true);
    const dismissed = localStorage.getItem(`push_dismissed_${linkId}`);
    if (dismissed) setPushDismissed(true);
  }, [linkId]);

  if (loading) return (
    <div className="min-h-screen bg-[#060B15] flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-[#FF9900] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#060B15] flex items-center justify-center px-4">
      <div className="text-center">
        <WarningCircle size={48} className="text-red-400 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-white mb-2">Link Not Found</h1>
        <p className="text-gray-400 text-sm">{error}</p>
      </div>
    </div>
  );

  const primaryColor = info?.primary_color || '#FF9900';

  return (
    <div className="min-h-screen bg-[#060B15] text-white" data-testid="client-submit-page">
      {/* Header */}
      <div className="border-b border-white/10 bg-white/[0.02] px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            {info?.company_logo ? (
              <img src={`${API_URL}${info.company_logo}`} alt="" className="h-8 rounded" />
            ) : (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: primaryColor + '20' }}>
                <Package size={16} style={{ color: primaryColor }} weight="duotone" />
              </div>
            )}
            <div>
              <p className="font-bold text-sm">{info?.company_name}</p>
              <p className="text-[10px] text-gray-500">{t('Tracking Portal', 'Portal de Tracking')}</p>
            </div>
          </div>
          <div className="text-right flex items-center gap-2">
            <PortalLanguageToggle variant="dark" />
            <p className="text-sm font-medium hidden md:block">{info?.customer_name}</p>
            <button
              onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/api/portal/dashboard-token/${linkId}`);
                  localStorage.setItem('portal_token', res.data.token);
                  localStorage.setItem('portal_customer', JSON.stringify(res.data.customer));
                  window.location.href = '/portal/dashboard';
                } catch (err) {
                  toast.error(t('Unable to open dashboard', 'No se pudo abrir el panel'));
                }
              }}
              className="h-9 px-3 rounded-lg text-xs font-bold text-white inline-flex items-center gap-1.5 transition-transform hover:scale-105"
              style={{ background: primaryColor }}
              data-testid="go-to-dashboard-btn"
              title={t('View packages and invoices', 'Ver paquetes y facturas')}
            >
              <Package size={14} weight="fill" />
              <span className="hidden sm:inline">{t('My Dashboard', 'Mi Panel')}</span>
              <span className="sm:hidden">{t('Dashboard', 'Panel')}</span>
              <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {/* ===== SUBMIT TRACKING - SIMPLIFIED ===== */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/[0.03] border border-white/10 rounded-2xl p-5 mb-6"
          data-testid="submit-form"
        >
          <h2 className="text-base font-bold mb-1" data-testid="tab-submit">{t('Add a Package', 'Agregar un Paquete')}</h2>
          <p className="text-xs text-gray-500 mb-4">{t('Paste your tracking number below', 'Pega tu número de tracking abajo')}</p>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <Input
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value.toUpperCase())}
              placeholder={t('Tracking number (UPS, FedEx, USPS...)', 'Número de tracking (UPS, FedEx, USPS...)')}
              className="flex-1 bg-white/5 border-white/10 text-white rounded-xl h-12 font-mono text-sm placeholder:text-gray-500"
              required
              data-testid="tracking-input"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="h-12 px-5 rounded-xl text-white font-medium"
              style={{ background: primaryColor }}
              data-testid="submit-btn"
            >
              {submitting ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus size={20} weight="bold" />
              )}
            </Button>
          </form>
        </motion.div>

        {/* ===== "GO TO DASHBOARD" PROMINENT CTA ===== */}
        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onClick={async () => {
            try {
              const res = await axios.post(`${API_URL}/api/portal/dashboard-token/${linkId}`);
              localStorage.setItem('portal_token', res.data.token);
              localStorage.setItem('portal_customer', JSON.stringify(res.data.customer));
              window.location.href = '/portal/dashboard';
            } catch {
              toast.error(t('Unable to open dashboard', 'No se pudo abrir el panel'));
            }
          }}
          className="w-full mb-6 rounded-2xl p-4 flex items-center justify-between gap-3 text-left transition-all hover:scale-[1.01]"
          style={{ background: `linear-gradient(135deg, ${primaryColor}25, ${primaryColor}10)`, border: `1px solid ${primaryColor}40` }}
          data-testid="dashboard-cta-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: primaryColor }}>
              <Package size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="text-white font-bold text-sm">{t('Go to My Dashboard', 'Ir a Mi Panel')}</h3>
              <p className="text-gray-400 text-[11px] leading-tight">{t('See all my packages & invoices', 'Ver mis paquetes y facturas')}</p>
            </div>
          </div>
          <ArrowRight size={18} className="text-white" weight="bold" />
        </motion.button>

        {/* ===== PUSH NOTIFICATION PROMPT ===== */}
        {!pushEnabled && !pushDismissed && 'Notification' in window && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-500/20 rounded-2xl p-4 mb-6"
            data-testid="push-notification-prompt"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: '#F43F5E20' }}>
                <BellRinging size={20} className="text-rose-400" weight="fill" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-bold text-sm mb-0.5">{t('Get Push Notifications', 'Recibe Notificaciones')}</h3>
                <p className="text-gray-400 text-xs leading-relaxed mb-3">
                  {t(
                    'Get instant alerts on your phone when a package status changes — even with the app closed.',
                    'Recibe alertas instantáneas en tu teléfono cuando cambie el estado de un paquete — incluso con la app cerrada.'
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={requestPushNotifications}
                    className="h-8 px-4 rounded-lg text-xs font-medium text-white"
                    style={{ background: primaryColor }}
                    data-testid="enable-push-btn"
                  >
                    <BellRinging size={14} className="mr-1.5" weight="bold" />
                    {t('Enable Notifications', 'Activar Notificaciones')}
                  </Button>
                  <button
                    onClick={() => { setPushDismissed(true); localStorage.setItem(`push_dismissed_${linkId}`, '1'); }}
                    className="text-gray-500 text-xs hover:text-gray-300 transition-colors"
                    data-testid="dismiss-push-btn"
                  >
                    {t('Not now', 'Ahora no')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
        {pushEnabled && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center gap-2 text-emerald-400 text-xs mb-4 px-1"
            data-testid="push-enabled-badge"
          >
            <CheckCircle size={14} weight="fill" />
            <span>{t('Push notifications enabled', 'Notificaciones activadas')}</span>
          </motion.div>
        )}

        {/* ===== PACKAGES LIST (Always Visible) ===== */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          data-testid="packages-list"
        >
          <div className="flex items-center justify-between mb-3" data-testid="tab-packages">
            <h3 className="text-sm font-bold text-gray-300">
              {t('My Packages', 'Mis Paquetes')} <span className="text-gray-600 font-normal">({packages.length})</span>
            </h3>
            {packages.length > 0 && (
              <button onClick={loadData} className="text-xs text-gray-500 hover:text-white transition-colors">
                {t('Refresh', 'Actualizar')}
              </button>
            )}
          </div>

          {packages.length === 0 ? (
            <div className="bg-white/[0.02] border border-dashed border-white/10 rounded-2xl p-8 text-center">
              <Package size={36} className="text-gray-700 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">{t('No packages yet', 'Aún no hay paquetes')}</p>
              <p className="text-gray-600 text-xs mt-1">{t('Submit your first tracking number above!', '¡Envía tu primer número de tracking arriba!')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {packages.map((pkg, i) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, x: -15 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white/[0.03] border border-white/10 rounded-2xl p-4"
                  data-testid={`package-${pkg.tracking_number}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-mono text-sm font-bold" style={{ color: primaryColor }}>{pkg.tracking_number}</p>
                    <span className="text-[10px] text-gray-500">{new Date(pkg.created_at).toLocaleDateString()}</span>
                  </div>
                  <StatusBar status={pkg.status} warehouseName={info?.warehouse_name} destinationName={info?.destination_name} />
                  {pkg.shipping_cost > 0 && (
                    <p className="mt-2 text-right text-xs text-gray-400">
                      {t('Cost', 'Costo')}: <span className="text-white font-semibold">${pkg.shipping_cost.toFixed(2)}</span>
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/5 bg-[#060B15]/90 backdrop-blur-md px-4 py-2 text-center" style={{ paddingBottom: showHomeBanner ? '120px' : undefined }}>
        <p className="text-gray-600 text-[10px]">Powered by <span className="text-gray-400 font-medium">Track My Package</span></p>
      </div>

      {/* Add to Home Screen Banner */}
      <AnimatePresence>
        {showHomeBanner && (
          <AddToHomeScreenBanner primaryColor={primaryColor} onDismiss={dismissBanner} />
        )}
      </AnimatePresence>

      {/* Step-by-step Tour */}
      {tourStep >= 0 && tourStep < TOUR_STEPS.length && (
        <TourOverlay
          step={TOUR_STEPS[tourStep]}
          totalSteps={TOUR_STEPS.length}
          currentStep={tourStep}
          primaryColor={primaryColor}
          onNext={() => {
            if (tourStep < TOUR_STEPS.length - 1) setTourStep(tourStep + 1);
            else setTourStep(-1);
          }}
          onSkip={() => setTourStep(-1)}
        />
      )}
    </div>
  );
}
